// Client lib imports
const { FriendlyRequestError } = require('client/lib/request')
// Client V2 common imports
const confirmModalActions = require('client-v2/common/containers/confirm-modal/actions')
const toastsActions = require('client-v2/common/containers/toasts/actions')
const articleModelActions = require('client-v2/common/models/article/actions')
const { collectionProfileHref } = require('client-v2/common/models/collection/actions')
const extension = require('client-v2/common/utils/extension')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const API = require('./api')
const selectors = require('./selectors')

// Action types

const SHOW = 'COLLECTION_SELECT_MODAL_SHOW'
const HIDE = 'COLLECTION_SELECT_MODAL_HIDE'
const ARTICLE_FAILED = 'COLLECTION_SELECT_MODAL_ARTICLE_FAILED'
const ARTICLE_LOADED = 'COLLECTION_SELECT_MODAL_ARTICLE_LOADED'
const ARTICLE_LOADING = 'COLLECTION_SELECT_MODAL_ARTICLE_LOADING'
const COLLECTIONS_FAILED = 'COLLECTION_SELECT_MODAL_COLLECTIONS_FAILED'
const COLLECTIONS_LOADED = 'COLLECTION_SELECT_MODAL_COLLECTIONS_LOADED'
const COLLECTIONS_LOADING = 'COLLECTION_SELECT_MODAL_COLLECTIONS_LOADING'
const COLLECTION_CREATED = 'COLLECTION_SELECT_MODAL_COLLECTION_CREATED'
const COLLECTION_UPDATED = 'COLLECTION_SELECT_MODAL_COLLECTION_UPDATED'
const CREATE_FAILED = 'COLLECTION_SELECT_MODAL_CREATE_FAILED'
const CREATE_LOADED = 'COLLECTION_SELECT_MODAL_CREATE_LOADED'
const CREATE_LOADING = 'COLLECTION_SELECT_MODAL_CREATE_LOADING'
const SET_FILTER_TEXT = 'COLLECTION_SELECT_MODAL_SET_FILTER_TEXT'
const UNMIX_FAILED = 'COLLECTION_SELECT_MODAL_UNMIX_FAILED'
const UNMIX_LOADED = 'COLLECTION_SELECT_MODAL_UNMIX_LOADED'
const UNMIX_LOADING = 'COLLECTION_SELECT_MODAL_UNMIX_LOADING'
const UNMIX_PROMPT_TOGGLE = 'COLLECTION_SELECT_MODAL_UNMIX_PROMPT_TOGGLE'

// Action enums

const COLLECTION_UPDATE_PENDING = 'COLLECTION_SELECT_MODAL_COLLECTION_UPDATE_PENDING'

// Action creators

function hide() {
  return {
    type: HIDE
  }
}

function setFilterText(filterText) {
  return {
    type: SET_FILTER_TEXT,
    payload: filterText
  }
}

function collectionCreated(collection) {
  return {
    type: COLLECTION_CREATED,
    payload: collection
  }
}

function collectionUpdated(collection) {
  return {
    type: COLLECTION_UPDATED,
    payload: collection
  }
}

function toggleUnmixPrompt(bool) {
  return {
    type: UNMIX_PROMPT_TOGGLE,
    payload: bool
  }
}

function showUnmixPrompt() {
  return toggleUnmixPrompt(true)
}

// Action dispatchers

function show({ article, url, anchorId, pageContext }) {
  return async function (dispatch, getState) {
    const state = getState()
    const showUrlId = article && article['url_id']
    const modalUrlId = selectors.urlId(state)
    if (selectors.isVisible(state)) {
      dispatch(hide())
    }
    // Treat `show` dispatcher as a toggle for the LWE modal
    if (modalUrlId && showUrlId === modalUrlId) {
      return
    }
    dispatch({
      type: SHOW,
      payload: { article, url, anchorId, pageContext }
    })
    await dispatch(fetch())
  }
}

function fetch() {
  return async function (dispatch, getState) {
    if (!selectors.isArticleLoaded(getState())) {
      await dispatch(fetchArticle())
    }
    if (!selectors.isArticleFailed(getState())) {
      await dispatch(fetchCollections())
    }
  }
}

function fetchArticle() {
  return async function (dispatch, getState) {
    const url = selectors.url(getState())
    dispatch({
      type: ARTICLE_LOADING
    })
    try {
      dispatch({
        type: ARTICLE_LOADED,
        payload: await API.extractArticleFromUrl(url)
      })
    } catch (err) {
      dispatch({
        type: ARTICLE_FAILED
      })
      const message = err instanceof FriendlyRequestError
      ? err.message
      : 'Something went wrong. Please try again.'
      dispatch(confirmModalActions.alert({
        heading: 'Could not extract post details',
        subheading: message
      }))
      throw err
    }
  }
}

function fetchCollections() {
  return async function (dispatch, getState) {
    const urlId = selectors.urlId(getState())
    dispatch({
      type: COLLECTIONS_LOADING
    })
    try {
      dispatch({
        type: COLLECTIONS_LOADED,
        payload: await API.fetchCollections(urlId)
      })
    } catch (err) {
      dispatch({
        type: COLLECTIONS_FAILED
      })
      const message = err instanceof FriendlyRequestError
      ? err.message
      : 'Something went wrong. Please try again.'
      dispatch(confirmModalActions.alert({
        heading: 'Could not fetch collections',
        subheading: message
      }))
      throw err
    }
  }
}

function cancelUnmix() {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isArticleUnmixable(state)) {
      return dispatch(toggleUnmixPrompt(false))
    }
    const urlId = selectors.urlId(state)
    const pageContext = selectors.pageContextToJS(state)
    dispatch({
      type: UNMIX_LOADING
    })
    try {
      const result = await API.addToMixedCollection({ urlId, pageContext })
      dispatch({
        type: UNMIX_LOADED
      })
      dispatch(articleModelActions.articleUpdated(result.post))
      dispatch(articleModelActions.articleMixed(result.post))
      extension.clearUrlCache(urlId)
      dispatch(toggleUnmixPrompt(false))
    } catch (err) {
      dispatch({
        type: UNMIX_FAILED
      })
      const message = err instanceof FriendlyRequestError
      ? err.message
      : 'Something went wrong. Please try again.'
      dispatch(confirmModalActions.alert({
        heading: 'Could not Mix this post to your profile',
        subheading: message
      }))
      throw err
    }
  }
}

function confirmUnmix() {
  return async function (dispatch, getState) {
    const state = getState()
    if (!selectors.isArticleUnmixable(state)) {
      return dispatch(hide())
    }
    const urlId = selectors.urlId(state)
    const pageContext = selectors.pageContextToJS(state)
    dispatch({
      type: UNMIX_LOADING
    })
    try {
      const result = await API.removeFromMixedCollection({ urlId, pageContext })
      dispatch(articleModelActions.articleUpdated(result.post))
      dispatch(articleModelActions.articleUnmixed(result.post))
      extension.clearUrlCache(urlId)
      dispatch({
        type: UNMIX_LOADED
      })
      dispatch(hide())
      dispatch(toastsActions.showNotice({
        message: 'Removed from your profile'
      }))
    } catch (err) {
      dispatch({
        type: UNMIX_FAILED
      })
      const message = err instanceof FriendlyRequestError
      ? err.message
      : 'Something went wrong. Please try again.'
      dispatch(confirmModalActions.alert({
        heading: 'Could not Unmix this post from your profile',
        subheading: message
      }))
      throw err
    }
  }
}

function toggleCollection({ collection }) {
  return async function (dispatch, getState) {
    if (collection.includesUrl === COLLECTION_UPDATE_PENDING) {
      return // This action is already in-progress
    }
    const state = getState()
    const urlId = selectors.urlId(state)
    const pageContext = selectors.pageContextToJS(state)
    dispatch(collectionUpdated({
      slug: collection.slug,
      includesUrl: COLLECTION_UPDATE_PENDING
    }))
    try {
      if (!collection.id) {
        // "Suggested" collection does not actually exist; create it
        const createResult = await API.createCollection({ collection, pageContext })
        dispatch(collectionCreated(createResult))
        return dispatch(toggleCollection({ collection: createResult }))
      }
      const toggleResult = collection.includesUrl
      ? await dispatch(removeFromCollection(collection))
      : await dispatch(addToCollection(collection))
      dispatch(articleModelActions.articleUpdated(toggleResult.post))
      if (!toggleResult.post.isCurated) {
        dispatch(toggleUnmixPrompt(true))
      }
      extension.clearUrlCache(urlId)
    } catch (err) {
      // Revert the includesUrl state to its previous value
      dispatch(collectionUpdated({
        slug: collection.slug,
        includesUrl: collection.includesUrl
      }))
      const heading = collection.includesUrl
      ? 'Remove from collection failed'
      : 'Add to collection failed'
      const subheading = err instanceof FriendlyRequestError
      ? err.message
      : 'Something went wrong. Please try again.'
      dispatch(confirmModalActions.alert({ heading, subheading }))
      throw err
    }
  }
}

function addToCollection(collection) {
  return async function (dispatch, getState) {
    const state = getState()
    const urlId = selectors.urlId(state)
    const pageContext = selectors.pageContextToJS(state)
    const result = await API.addToCollection({
      urlId,
      collectionId: collection.id,
      pageContext
    })
    dispatch(collectionUpdated({
      slug: collection.slug,
      includesUrl: result.includesUrl,
      'posts_count': result.mix['posts_count'],
      'modified_at': new Date().toISOString()
    }))
    dispatch(articleModelActions.articleAddedToCollection(result.post, collection.id))
    dispatch(showAddedToCollectionToast(collection))
    return result
  }
}

function removeFromCollection(collection) {
  return async function (dispatch, getState) {
    const state = getState()
    const urlId = selectors.urlId(state)
    const pageContext = selectors.pageContextToJS(state)
    const result = await API.removeFromCollection({
      urlId,
      collectionId: collection.id,
      pageContext
    })
    dispatch(articleModelActions.articleRemovedFromCollection(result.post, collection.id))
    if (result.mixDeleted) {
      // Turn the collection back into a "Suggested" item
      dispatch(collectionUpdated({
        slug: collection.slug,
        id: 0,
        includesUrl: result.includesUrl,
        'posts_count': 0,
        'modified_at': ''
      }))
      dispatch(showRemovedFromDeletedCollectionToast(collection))
    } else {
      dispatch(collectionUpdated({
        slug: collection.slug,
        includesUrl: result.includesUrl,
        'posts_count': result.mix['posts_count'],
        'modified_at': new Date().toISOString()
      }))
      dispatch(showRemovedFromCollectionToast(collection))
    }
    if (!result.post.isMixed) {
      dispatch(articleModelActions.articleUnmixed(result.post))
    }
    return result
  }
}

function createCollectionFromFilter() {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isCreatePending(state)) {
      return // Collection creation is already in-progress
    }
    if (!selectors.canCreateFromFilter(state)) {
      return // Nothing to create
    }
    const pageContext = selectors.pageContextToJS(state)
    const modal = selectors.containerState(state)
    const slug = selectors.filterSlug(state)
    const collectionIndex = modal.findCollectionIndex({ slug })
    if (collectionIndex !== -1) {
      // Collection already exists; toggle instead of creating
      return dispatch(toggleCollection({
        collection: modal.collections.get(collectionIndex)
      }))
    }
    try {
      const collection = await API.createCollection({
        collection: { name: selectors.filterText(state) },
        pageContext
      })
      dispatch({
        type: CREATE_LOADED,
        payload: collection
      })
      return dispatch(toggleCollection({ collection }))
    } catch (err) {
      dispatch({
        type: CREATE_FAILED
      })
      const message = err instanceof FriendlyRequestError
      ? err.message
      : 'Something went wrong. Please try again.'
      dispatch(confirmModalActions.alert({
        heading: 'Collection creation failed',
        subheading: message
      }))
      throw err
    }
  }
}

// Exports

module.exports = {
  // Action types
  SHOW,
  HIDE,
  ARTICLE_FAILED,
  ARTICLE_LOADED,
  ARTICLE_LOADING,
  COLLECTIONS_FAILED,
  COLLECTIONS_LOADED,
  COLLECTIONS_LOADING,
  COLLECTION_CREATED,
  COLLECTION_UPDATED,
  CREATE_FAILED,
  CREATE_LOADED,
  CREATE_LOADING,
  SET_FILTER_TEXT,
  UNMIX_FAILED,
  UNMIX_LOADED,
  UNMIX_LOADING,
  UNMIX_PROMPT_TOGGLE,
  // Action enums
  COLLECTION_UPDATE_PENDING,
  // Action creators
  hide,
  setFilterText,
  showUnmixPrompt,
  // Action dispatchers
  cancelUnmix,
  confirmUnmix,
  createCollectionFromFilter,
  fetch,
  show,
  toggleCollection
}

// Toast helpers

function showAddedToCollectionToast(collection) {
  return toastsActions.showNotice({
    message: () => tag([
      'Added to your ',
      tag('a', {
        href: collectionProfileHref(collection)
      },
        collection.name
      ),
      ' collection'
    ])
  })
}

function showRemovedFromCollectionToast(collection) {
  return toastsActions.showNotice({
    message: () => tag([
      'Removed from your ',
      tag('a', {
        href: collectionProfileHref(collection)
      },
        collection.name
      ),
      ' collection'
    ])
  })
}

function showRemovedFromDeletedCollectionToast(collection) {
  return toastsActions.showNotice({
    message: () => tag([
      'Removed from your ',
      tag('b', collection.name),
      ' collection'
    ])
  })
}
